import React, { useState } from 'react';
import { Container, Form, Button, Alert, Card } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

const SuggestTool = () => {
  const [formData, setFormData] = useState({
    toolName: '',
    description: '',
    useCase: '',
    email: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    try {
      const response = await fetch(process.env.REACT_APP_WEBHOOK_SUGGEST_TOOL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString(),
          source: 'excel-toolbox-web'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to submit suggestion');
      }
      
      setSubmitted(true);
      setFormData({
        toolName: '',
        description: '',
        useCase: '',
        email: ''
      });
    } catch (err) {
      setError(err.message || 'Failed to submit your suggestion. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Suggest a Tool - Excel Toolbox</title>
        <meta name="description" content="Suggest a new Excel tool that would help improve your workflow. We're always looking for new ideas to make Excel work easier." />
      </Helmet>

      <Container className="py-5">
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold mb-3">
            Suggest a Tool
          </h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
            Have an Excel challenge? We want to hear about it. Your suggestions help us build better tools for everyone.
          </p>
        </div>

        {submitted ? (
          <Card className="shadow-sm border-0 p-4 mx-auto" style={{ maxWidth: '600px' }}>
            <div className="text-center">
              <div className="text-success mb-3">
                <i className="bi bi-check-circle-fill fs-1"></i>
              </div>
              <h2 className="h4 mb-3">Thank You for Your Suggestion!</h2>
              <p className="text-muted mb-4">
                We appreciate your input and will carefully consider your suggestion for future development.
              </p>
              <Button variant="outline-success" onClick={() => setSubmitted(false)}>
                Suggest Another Tool
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="shadow-sm border-0 p-4 mx-auto" style={{ maxWidth: '600px' }}>
            {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
            
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label>What would you like to name this tool?</Form.Label>
                <Form.Control
                  type="text"
                  name="toolName"
                  value={formData.toolName}
                  onChange={handleChange}
                  placeholder="e.g., Excel Column Formatter"
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>What should this tool do?</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the main functionality you'd like to see..."
                  rows={3}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>What problem would this tool solve for you?</Form.Label>
                <Form.Control
                  as="textarea"
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  placeholder="Tell us about your specific use case or challenge..."
                  rows={3}
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Email address (optional)</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="We'll notify you when we build your tool"
                  disabled={isSubmitting}
                />
                <Form.Text className="text-muted">
                  We'll never share your email with anyone else.
                </Form.Text>
              </Form.Group>

              <div className="d-grid">
                <Button 
                  variant="success" 
                  type="submit" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Suggestion'
                  )}
                </Button>
              </div>
            </Form>
          </Card>
        )}
      </Container>
    </>
  );
};

export default SuggestTool;
